"use client"

import * as React from "react";
import useEmblaCarousel, {
    type UseEmblaCarouselType,
} from "embla-carousel-react";

import { cn } from "@/lib/utils";
import { Button, type ButtonProps } from "@/components/ui/button";
import { SvgLeft, SvgRight } from "../icons-eataly";
import {
    areCarouselPageSnapsEqual,
    buildCarouselPageSnapIndices,
    canScrollToNextPage,
    canScrollToPrevPage,
    getCurrentPageIndexFromSnap,
    scrollToNextPage,
    scrollToPrevPage,
} from "@/lib/carousel-page-scroll";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselNavigationMode = "snap" | "page"

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: "horizontal" | "vertical"
  setApi?: (api: CarouselApi) => void
  /**
   * `snap` (default): one slide per prev/next.
   * `page`: advance by viewport — next page starts at the first tile not fully visible.
   */
  navigationMode?: CarouselNavigationMode
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
  navigationMode: CarouselNavigationMode
  pageSnapIndices: number[]
  isComputingPageSnapsRef: React.RefObject<boolean>
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

/**
 * Inert context used when carousel primitives render without a provider.
 * Throwing here used to hang React 19 SSR: a parent Suspense tear-down (e.g. PD
 * component preload) left CarouselItem/Content briefly outside <Carousel />, and
 * `retryNode` spun on the thrown error at ~100% CPU with an empty response body.
 */
const INERT_CAROUSEL_CONTEXT: CarouselContextProps = {
  carouselRef: () => {},
  api: undefined,
  scrollPrev: () => {},
  scrollNext: () => {},
  canScrollPrev: false,
  canScrollNext: false,
  navigationMode: "snap",
  pageSnapIndices: [],
  isComputingPageSnapsRef: { current: false },
  orientation: "horizontal",
}

function useCarousel() {
  const context = React.useContext(CarouselContext)
  if (!context) {
    // Not throwing keeps SSR alive during transient Suspense tear-down (see above).
    // In dev, surface the missing provider so a genuine misuse — a CarouselItem/Content
    // used outside <Carousel /> — is still visible instead of silently inert.
    if (import.meta.env.DEV) {
      console.warn(
        "useCarousel: no <Carousel /> provider found; falling back to an inert context. " +
          "If this is not a transient Suspense tear-down, a carousel primitive is being used outside <Carousel />."
      )
    }
    return INERT_CAROUSEL_CONTEXT
  }
  return context
}

function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  navigationMode = "snap",
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & CarouselProps) {
  const { t } = useTranslation("carousel")
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins
  )
  const [canScrollPrev, setCanScrollPrev] = React.useState(false)
  const [canScrollNext, setCanScrollNext] = React.useState(false)
  const [pageSnapIndices, setPageSnapIndices] = React.useState<number[]>([])
  const pageSnapIndicesRef = React.useRef<number[]>([])
  const isComputingPageSnapsRef = React.useRef(false)

  const syncPageSnaps = React.useCallback((emblaApi: NonNullable<CarouselApi>) => {
    if (navigationMode !== "page") {
      pageSnapIndicesRef.current = []
      setPageSnapIndices((prev) => (prev.length === 0 ? prev : []))
      return
    }

    isComputingPageSnapsRef.current = true
    try {
      const snaps = buildCarouselPageSnapIndices(emblaApi)
      pageSnapIndicesRef.current = snaps
      setPageSnapIndices((prev) =>
        areCarouselPageSnapsEqual(prev, snaps) ? prev : snaps
      )
    } finally {
      isComputingPageSnapsRef.current = false
    }
  }, [navigationMode])

  const updateScrollButtons = React.useCallback(
    (emblaApi: NonNullable<CarouselApi>) => {
      if (navigationMode === "page") {
        const snaps = pageSnapIndicesRef.current
        setCanScrollPrev(canScrollToPrevPage(emblaApi, snaps))
        setCanScrollNext(canScrollToNextPage(emblaApi, snaps))
        return
      }
      setCanScrollPrev(emblaApi.canScrollPrev())
      setCanScrollNext(emblaApi.canScrollNext())
    },
    [navigationMode]
  )

  const scrollPrev = React.useCallback(() => {
    if (!api) return
    if (navigationMode === "page") {
      scrollToPrevPage(api, pageSnapIndicesRef.current)
      return
    }
    api.scrollPrev()
  }, [api, navigationMode])

  const scrollNext = React.useCallback(() => {
    if (!api) return
    if (navigationMode === "page") {
      scrollToNextPage(api, pageSnapIndicesRef.current)
      return
    }
    api.scrollNext()
  }, [api, navigationMode])

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault()
        scrollPrev()
      } else if (event.key === "ArrowRight") {
        event.preventDefault()
        scrollNext()
      }
    },
    [scrollPrev, scrollNext]
  )

  React.useEffect(() => {
    if (!api || !setApi) return
    setApi(api)
  }, [api, setApi])

  React.useEffect(() => {
    if (!api) return

    const handleReInit = (emblaApi: CarouselApi) => {
      if (!emblaApi) return
      syncPageSnaps(emblaApi)
      updateScrollButtons(emblaApi)
    }

    const handleSelect = (emblaApi: CarouselApi) => {
      if (!emblaApi || isComputingPageSnapsRef.current) return
      updateScrollButtons(emblaApi)
    }

    handleReInit(api)
    api.on("reInit", handleReInit)
    api.on("select", handleSelect)
    api.on("scroll", handleSelect)
    api.on("settle", handleSelect)
    api.on("resize", handleReInit)

    return () => {
      api.off("reInit", handleReInit)
      api.off("select", handleSelect)
      api.off("scroll", handleSelect)
      api.off("settle", handleSelect)
      api.off("resize", handleReInit)
    }
  }, [api, syncPageSnaps, updateScrollButtons])

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation:
          orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
        navigationMode,
        pageSnapIndices,
        isComputingPageSnapsRef,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn("relative", className)}
        role="region"
        aria-roledescription={t("roleDescriptionCarousel")}
        data-slot="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

function CarouselContent({
  className,
  viewportClassName,
  ...props
}: React.ComponentProps<"div"> & { viewportClassName?: string }) {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div
      ref={carouselRef}
      className={cn("overflow-hidden", viewportClassName)}
      data-slot="carousel-content"
    >
      <div
        className={cn(
          "flex",
          orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
          className
        )}
        {...props}
      />
    </div>
  )
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  const { orientation } = useCarousel()
  const { t } = useTranslation("carousel")

  return (
    <div
      role="group"
      aria-roledescription={t("roleDescriptionSlide")}
      data-slot="carousel-item"
      className={cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className
      )}
      {...props}
    />
  )
}

type CarouselDotsProps = {
  /** Wrapper class name. */
  className?: string
  /** Dot button base class name (applied to all dots). */
  dotClassName?: string
  /** Dot button class name when selected. */
  activeDotClassName?: string
  /** Dot button class name when not selected. */
  inactiveDotClassName?: string
  /** Build an accessible label per dot. */
  getDotAriaLabel?: (index: number) => string
  hideIfNoScroll?: boolean
}

function CarouselDots({
  className,
  dotClassName,
  activeDotClassName,
  inactiveDotClassName,
  getDotAriaLabel: getDotAriaLabelProp,
  hideIfNoScroll = false,
}: CarouselDotsProps) {
  const { api, navigationMode, pageSnapIndices, canScrollPrev, canScrollNext, isComputingPageSnapsRef } =
    useCarousel()
  const isScrollableAtAll = canScrollPrev || canScrollNext
  const [dotCount, setDotCount] = React.useState(0)
  const [selectedDot, setSelectedDot] = React.useState(0)
  const [pageScrollTargets, setPageScrollTargets] = React.useState<number[]>([])
  const [areDotsIndexSlideGroups, setAreDotsIndexSlideGroups] = React.useState(false)
  const { t } = useTranslation("carousel")

  const resolveDotAriaLabel = useCallback(
    (index: number) => {
      if (typeof getDotAriaLabelProp === "function") {
        return getDotAriaLabelProp(index)
      }
      if (areDotsIndexSlideGroups) {
        return t("goToGroup", { index: index + 1 })
      }
      return t("goToSlide", { index: index + 1 })
    },
    [getDotAriaLabelProp, areDotsIndexSlideGroups, t]
  )

  React.useEffect(() => {
    if (!api) {
      setDotCount(0)
      setSelectedDot(0)
      setPageScrollTargets([])
      setAreDotsIndexSlideGroups(false)
      return
    }

    const sync = () => {
      if (isComputingPageSnapsRef.current) return

      setAreDotsIndexSlideGroups((api.slidesInView()?.length ?? 0) > 1)

      if (navigationMode === "page") {
        const snaps = pageSnapIndices
        setPageScrollTargets((prev) =>
          areCarouselPageSnapsEqual(prev, snaps) ? prev : snaps
        )
        setDotCount(snaps.length)
        setSelectedDot(getCurrentPageIndexFromSnap(api, snaps))
        return
      }

      setPageScrollTargets([])
      setDotCount(api.scrollSnapList().length)
      setSelectedDot(api.selectedScrollSnap())
    }

    sync()
    api.on("reInit", sync)
    api.on("select", sync)
    api.on("scroll", sync)
    api.on("settle", sync)
    api.on("resize", sync)

    return () => {
      api.off("reInit", sync)
      api.off("select", sync)
      api.off("scroll", sync)
      api.off("settle", sync)
      api.off("resize", sync)
    }
  }, [api, navigationMode, pageSnapIndices, isComputingPageSnapsRef])

  if (!api || dotCount <= 1 || (hideIfNoScroll && !isScrollableAtAll)) return null

  return (
    <div
      className={cn("flex justify-center gap-2", className)}
      data-slot="carousel-dots"
    >
      {Array.from({ length: dotCount }, (_, i) => (
        <button
          key={i}
          type="button"
          aria-label={resolveDotAriaLabel(i)}
          aria-current={selectedDot === i ? "true" : undefined}
          className={cn(
            "h-2 w-2 shrink-0 rounded-full transition-colors cursor-pointer",
            dotClassName,
            selectedDot === i ? activeDotClassName : inactiveDotClassName
          )}
          onClick={() =>
            api.scrollTo(
              navigationMode === "page"
                ? (pageScrollTargets[i] ?? i)
                : i
            )
          }
        />
      ))}
    </div>
  )
}

function CarouselPrevious({
  className,
  size = "icon",
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  hideIfNoScroll = false,
  ...props
}: Omit<ButtonProps, "iconOnly" | "children" | "onClick"> & { hideIfNoScroll?: boolean }) {
  const { orientation, scrollPrev, canScrollPrev, canScrollNext } = useCarousel()
  const { t } = useTranslation("carousel")
  const isScrollableAtAll = canScrollPrev || canScrollNext
  if (hideIfNoScroll && !isScrollableAtAll) return null

  const labelledProps =
    ariaLabel?.trim?.()
      ? { "aria-label": ariaLabel }
      : ariaLabelledBy?.trim?.()
        ? { "aria-labelledby": ariaLabelledBy }
        : { "aria-label": t("previousSlide") }

  return (
    <Button
      data-slot="carousel-previous"
      hierarchy="secondary"
      iconOnly
      size={size}
      className={cn(
        "absolute cursor-pointer",
        orientation === "horizontal"
          ? "top-1/2 -left-12 -translate-y-1/2"
          : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...labelledProps}
      {...props}
    >
      <SvgLeft />
      <span className="sr-only">{t("previousSlide")}</span>
    </Button>
  )
}

function CarouselNext({
  className,
  size = "icon",
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  hideIfNoScroll = false,
  ...props
}: Omit<ButtonProps, "iconOnly" | "children" | "onClick"> & { hideIfNoScroll?: boolean }) {
  const { orientation, scrollNext, canScrollPrev, canScrollNext } = useCarousel()
  const { t } = useTranslation("carousel")
  const isScrollableAtAll = canScrollPrev || canScrollNext
  if (hideIfNoScroll && !isScrollableAtAll) return null

  const labelledProps =
    ariaLabel?.trim?.()
      ? { "aria-label": ariaLabel }
      : ariaLabelledBy?.trim?.()
        ? { "aria-labelledby": ariaLabelledBy }
        : { "aria-label": t("nextSlide") }

  return (
    <Button
      data-slot="carousel-next"
      hierarchy="secondary"
      iconOnly
      size={size}
      className={cn(
        "absolute cursor-pointer",
        orientation === "horizontal"
          ? "top-1/2 -right-12 -translate-y-1/2"
          : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...labelledProps}
      {...props}
    >
      <SvgRight />
      <span className="sr-only">{t("nextSlide")}</span>
    </Button>
  )
}

export {
  type CarouselApi,
  type CarouselNavigationMode,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselDots,
  CarouselPrevious,
  CarouselNext,
}
